import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  passwordProblemMessage,
  personalWords,
  type PasswordProblem,
  type SessionPayload,
} from '@wr/shared';
import { ApiError, errorMessage, post } from '../api.js';
import {
  NewPasswordFields,
  Notice,
  clearHash,
  newPasswordReady,
  tokenFromHash,
} from '../components/ui.js';
import { useLoaded } from '../session.js';
import { AuthLayout } from './AuthLayout.js';

/** Opened from the invitation email: choose a password, then set up the app. */
export function Welcome() {
  const { rules, setSession } = useLoaded();
  const navigate = useNavigate();
  const [token] = useState(tokenFromHash);
  const [invitee, setInvitee] = useState<{
    email: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const [failed, setFailed] = useState<string | null>(
    token ? null : errorMessage(new ApiError(400, 'invalid_token')),
  );
  const [value, setValue] = useState({ password: '', confirm: '' });
  const [serverProblems, setServerProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    clearHash();
    if (!token) return;
    post<{ email: string; firstName: string; lastName: string }>('/auth/invitation/check', {
      token,
    })
      .then(setInvitee)
      .catch((err: unknown) => setFailed(errorMessage(err)));
  }, [token]);

  const personal = invitee ? personalWords(invitee) : [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setServerProblems([]);
    try {
      const session = await post<SessionPayload>('/auth/invitation/accept', {
        token,
        password: value.password,
      });
      setSession(session);
      navigate(session.state === 'code' ? '/sign-in/code' : '/set-up-app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') {
        const problems = (err.body.problems as PasswordProblem[] | undefined) ?? [];
        setServerProblems(problems.map((p) => passwordProblemMessage(p, rules.password)));
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (failed) {
    return (
      <AuthLayout title="Invitation">
        <Notice kind="error">{failed}</Notice>
        <p>Ask an administrator of the portal to send you a new invitation.</p>
        <p className="small">
          <Link to="/sign-in">Go to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={invitee ? `Welcome, ${invitee.firstName}` : 'Welcome'}>
      {invitee && (
        <p className="muted">
          Choose the password of your account <strong>{invitee.email}</strong>. Next, you will set
          up two-step sign-in with an authenticator app on your phone.
        </p>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        <NewPasswordFields
          policy={rules.password}
          personal={personal}
          value={value}
          onChange={setValue}
          serverProblems={serverProblems}
        />
        <button
          type="submit"
          className="btn primary block"
          disabled={busy || !invitee || !newPasswordReady(value, rules.password, personal)}
        >
          Choose this password
        </button>
      </form>
    </AuthLayout>
  );
}
